/**
 * Passphrase Setup Modal
 *
 * First-time encryption passphrase setup flow.
 * Guides users through creating a secure passphrase for E2EE.
 *
 * Features:
 * - Passphrase strength validation
 * - Real-time strength indicator
 * - Confirmation field
 * - Security warnings
 * - Required acknowledgment checkbox
 *
 * @fileoverview Passphrase setup component for encryption initialization
 */

import { AlertTriangle, Check, Eye, EyeOff, Info, Shield, X } from 'lucide-react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/auth-context';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface PassphraseSetupProps {
  onComplete: (passphrase: string) => Promise<void>;
  onCancel?: () => void;
}

interface ValidationResult {
  valid: boolean;
  strength: 'weak' | 'medium' | 'strong' | 'very-strong';
  errors: string[];
  score: number; // 0-100
}

/**
 * Validate Passphrase Strength
 *
 * Checks passphrase against security requirements and calculates strength.
 *
 * Requirements:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */
function validatePassphrase(passphrase: string): ValidationResult {
  const errors: string[] = [];
  let score = 0;

  // Length check
  if (passphrase.length === 0) {
    return {
      valid: false,
      strength: 'weak',
      errors: ['Passphrase is required'],
      score: 0,
    };
  }

  if (passphrase.length < 12) {
    errors.push('At least 12 characters required');
  } else {
    score += 20;
    // Bonus for extra length
    score += Math.min(20, (passphrase.length - 12) * 2);
  }

  // Uppercase check
  if (!/[A-Z]/.test(passphrase)) {
    errors.push('At least one uppercase letter');
  } else {
    score += 15;
  }

  // Lowercase check
  if (!/[a-z]/.test(passphrase)) {
    errors.push('At least one lowercase letter');
  } else {
    score += 15;
  }

  // Number check
  if (!/[0-9]/.test(passphrase)) {
    errors.push('At least one number');
  } else {
    score += 15;
  }

  // Special character check
  if (!/[^A-Za-z0-9]/.test(passphrase)) {
    errors.push('At least one special character (!@#$%^&*)');
  } else {
    score += 15;
  }

  // Check for common patterns (weak)
  const commonPatterns = ['password', '123456', 'qwerty', 'abc123', 'letmein'];
  const lowerPassphrase = passphrase.toLowerCase();
  if (commonPatterns.some((pattern) => lowerPassphrase.includes(pattern))) {
    errors.push('Contains common password pattern');
    score = Math.max(0, score - 30);
  }

  // Determine strength
  let strength: ValidationResult['strength'];
  if (score < 40) {
    strength = 'weak';
  } else if (score < 60) {
    strength = 'medium';
  } else if (score < 80) {
    strength = 'strong';
  } else {
    strength = 'very-strong';
  }

  return {
    valid: errors.length === 0 && passphrase.length >= 12,
    strength,
    errors,
    score: Math.min(100, score),
  };
}

export function PassphraseSetup({ onComplete, onCancel }: PassphraseSetupProps) {
  const { user } = useAuth();
  const isAttorney = user?.role === 'ATTORNEY';
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({
    passphrase: false,
    confirm: false,
  });

  const validation = useMemo(() => {
    return validatePassphrase(passphrase);
  }, [passphrase]);
  const passwordsMatch = passphrase === confirmPassphrase && confirmPassphrase.length > 0;

  // Helper functions to get strength colors (using inline styles to avoid Tailwind issues)
  const getStrengthBarColor = (strength: ValidationResult['strength']) => {
    switch (strength) {
      case 'weak':
        return '#ef4444'; // red-500
      case 'medium':
        return '#eab308'; // yellow-500
      case 'strong':
        return '#A66B32'; // cognac accent (was blue-500 pre-2026-08-17 palette refresh)
      case 'very-strong':
        return '#22c55e'; // green-500
      default:
        return '#6b7280'; // gray-500
    }
  };

  const getStrengthTextColor = (strength: ValidationResult['strength']) => {
    switch (strength) {
      case 'weak':
        return '#dc2626'; // red-600
      case 'medium':
        return '#ca8a04'; // yellow-600
      case 'strong':
        return '#8A5626'; // deeper cognac (was blue-600 pre-2026-08-17 palette refresh)
      case 'very-strong':
        return '#16a34a'; // green-600
      default:
        return '#4b5563'; // gray-600
    }
  };

  const strengthLabels = {
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    'very-strong': 'Very Strong',
  };

  const canSubmit = validation.valid && passwordsMatch && acknowledged && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      if (!validation.valid) {
        toast.error('Please fix passphrase requirements');
      } else if (!passwordsMatch) {
        toast.error('Passphrases do not match');
      } else if (!acknowledged) {
        toast.error('Please acknowledge you understand the risks');
      }
      return;
    }

    setIsSubmitting(true);

    try {
      await onComplete(passphrase);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to set up encryption');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 0, 0, 0.1)' }}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#EAE0CB] rounded-lg">
              <Shield className="h-6 w-6 text-[#A66B32]" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Set Up Encryption</h2>
          </div>
          <p className="text-sm text-gray-600">
            Create a secure passphrase to protect your case data
          </p>
          {isAttorney && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-[#EAE0CB]/40 border border-[#EAE0CB] px-3 py-2.5">
              <Info className="h-4 w-4 text-[#A66B32] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-[#3A2718]">
                <span className="font-medium">Tip:</span> You can use the same passphrase as your
                account password for convenience. This passphrase encrypts your case data locally.
              </p>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Passphrase Input */}
          <div>
            <Label htmlFor="passphrase" className="text-gray-700 font-medium">
              Passphrase *
            </Label>
            <div className="relative mt-1">
              <Input
                id="passphrase"
                type={showPassphrase ? 'text' : 'password'}
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                onBlur={() => setTouched({ ...touched, passphrase: true })}
                className="pr-12 bg-white border-gray-300 text-gray-900 focus:border-[#A66B32] focus:ring-[#A66B32]/30/20"
                placeholder="Enter a strong passphrase"
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassphrase(!showPassphrase)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#A66B32]/30 focus:ring-offset-1"
                tabIndex={-1}
                aria-label={showPassphrase ? 'Hide passphrase' : 'Show passphrase'}
              >
                {showPassphrase ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {/* Strength Indicator */}
            {passphrase.length > 0 && (
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Strength:</span>
                  <span
                    className="font-medium"
                    style={{ color: getStrengthTextColor(validation.strength) }}
                  >
                    {strengthLabels[validation.strength]} ({validation.score}%)
                  </span>
                </div>
                <div
                  className="w-full bg-gray-200 rounded-full overflow-hidden"
                  style={{ height: '8px' }}
                >
                  <div
                    className="h-full transition-all duration-300"
                    style={{
                      width: `${validation.score}%`,
                      backgroundColor: getStrengthBarColor(validation.strength),
                      minWidth: validation.score > 0 ? '5%' : '0%',
                    }}
                  />
                </div>
              </div>
            )}

            {/* Validation Errors */}
            {touched.passphrase && validation.errors.length > 0 && (
              <div className="mt-2 space-y-1">
                {validation.errors.map((error, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: index keys are the established behavior for these order-stable lists
                  <div key={index} className="flex items-center gap-1 text-xs text-red-600">
                    <X className="h-3 w-3" />
                    <span>{error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confirm Passphrase Input */}
          <div>
            <Label htmlFor="confirm" className="text-gray-700 font-medium">
              Confirm Passphrase *
            </Label>
            <div className="relative mt-1">
              <Input
                id="confirm"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassphrase}
                onChange={(e) => setConfirmPassphrase(e.target.value)}
                onBlur={() => setTouched({ ...touched, confirm: true })}
                className="pr-12 bg-white border-gray-300 text-gray-900 focus:border-[#A66B32] focus:ring-[#A66B32]/30/20"
                placeholder="Re-enter your passphrase"
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#A66B32]/30 focus:ring-offset-1"
                tabIndex={-1}
                aria-label={showConfirm ? 'Hide confirmation' : 'Show confirmation'}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {/* Match Indicator */}
            {confirmPassphrase.length > 0 && (
              <div className="mt-2">
                {passwordsMatch ? (
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <Check className="h-3 w-3" />
                    <span>Passphrases match</span>
                  </div>
                ) : (
                  touched.confirm && (
                    <div className="flex items-center gap-1 text-xs text-red-600">
                      <X className="h-3 w-3" />
                      <span>Passphrases do not match</span>
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          {/* Security Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <h3 className="font-semibold text-amber-900 text-sm">
                  Important: Passphrase Recovery
                </h3>
                <ul className="text-xs text-amber-800 space-y-1">
                  <li>
                    • Your passphrase is <strong>NOT</strong> stored on our servers
                  </li>
                  <li>
                    • If you lose your passphrase, your data <strong>CANNOT</strong> be recovered
                  </li>
                  <li>• Write down your passphrase in a secure location</li>
                  <li>• Consider using a password manager</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Acknowledgment Checkbox */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="acknowledge"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-[#A66B32] focus:ring-[#A66B32]/30"
              disabled={isSubmitting}
            />
            <label htmlFor="acknowledge" className="text-sm text-gray-700 cursor-pointer">
              I understand and have saved my passphrase securely. I acknowledge that{' '}
              <strong>ExpertMD cannot recover my data</strong> if I lose this passphrase.
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 bg-[#A66B32] text-white hover:bg-[#241610] disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2 justify-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Setting up...
                </div>
              ) : (
                'Set Up Encryption'
              )}
            </Button>
          </div>

          {/* Help Text */}
          <p className="text-xs text-gray-500 text-center">
            Your passphrase encrypts all sensitive case data. Keep it safe!
          </p>
        </form>
      </div>
    </div>
  );
}
